'use client'

import { useEffect, useState } from 'react'

type Option = { id: string; name: string }

// Same field and button look as the checkout's own (AddressFields, Pay).
const SELECT_CLASS =
  'min-w-0 flex-1 min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-base text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]'
const SEND_CLASS =
  'cta-primary min-h-[44px] rounded-[var(--layout-button-radius,var(--brand-radius,0.75rem))] px-5 text-sm font-medium transition-opacity disabled:opacity-50'

/**
 * One quiet, optional question after a purchase or booking, shown only when
 * the store turned it on and the sale has no partner yet. The answer waits for
 * the dealer's approval before anyone is paid.
 */
export default function ReferralQuestion({ kind, saleId }: { kind: 'order' | 'booking'; saleId: string }) {
  const [options, setOptions] = useState<Option[] | null>(null)
  const [choice, setChoice] = useState('')
  const [state, setState] = useState<'ask' | 'sending' | 'done' | 'failed' | 'dismissed'>('ask')

  useEffect(() => {
    let live = true
    fetch(`/api/partner-referral?kind=${kind}&sale=${encodeURIComponent(saleId)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { partners?: Option[] | null } | null) => {
        if (live) setOptions(j?.partners?.length ? j.partners : null)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [kind, saleId])

  if (!options || state === 'dismissed') return null
  if (state === 'done') {
    return (
      <p role="status" aria-live="polite" className="text-sm text-[var(--color-text-secondary)]">
        Thanks, we&rsquo;ll pass that on.
      </p>
    )
  }

  async function send() {
    if (!choice) return
    setState('sending')
    const ok = await fetch('/api/partner-referral', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, saleId, partnerId: choice }),
    })
      .then(async (r) => r.ok && ((await r.json()) as { ok?: boolean }).ok === true)
      .catch(() => false)
    setState(ok ? 'done' : 'failed')
  }

  return (
    <div className="space-y-2 rounded-md border border-[var(--color-border)] p-4">
      <label htmlFor="referral-partner" className="block text-sm font-medium text-[var(--color-text)]">
        Did someone refer you?
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <select id="referral-partner" value={choice} onChange={(e) => setChoice(e.target.value)} className={SELECT_CLASS}>
          <option value="">Choose…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => void send()} disabled={!choice || state === 'sending'} className={SEND_CLASS}>
          Send
        </button>
        <button
          type="button"
          onClick={() => setState('dismissed')}
          className="min-h-[44px] px-2 text-sm text-[var(--color-text-muted)] underline underline-offset-2"
        >
          No one
        </button>
      </div>
      {state === 'failed' && (
        <p role="status" aria-live="polite" className="text-sm text-[var(--color-text-muted)]">
          We couldn&rsquo;t send that. Try again in a moment.
        </p>
      )}
    </div>
  )
}
